import SettingsForm from "./SettingsForm.js";
import { loadLunrSearch } from "../helpers.js";
import BottomSheet from "./BottomSheet.js";
import MovieList from "./MovieList.js";

/**
 * @typedef {{
 *   sanitizeTerms: (query: string) => string,
 *   search: (query: string) => Array<{ref: string, score: number}>,
 *   getDocumentByRef: (ref: string) => any
 * }} LunrSearch
 */

/**
 * @typedef {{
 *   preloadScriptId: string,
 *   minimumQueryLength: number,
 *   maxMatchesToShow: number|null
 * }} HeaderSearchOptions
 */

/**
 * @typedef {import("./BottomSheet.js").BottomSheetComponent} HeaderSearchComponent
 */

/**
 * Creates a header search component
 * @param {HTMLElement} element - The search container element
 * @param {HeaderSearchOptions} options - Configuration options
 * @returns {HeaderSearchComponent}
 */
const HeaderSearch = (element, options) => {
  const MESSAGES = {
    button: "Összes találat megjelenítése",
    default: `<strong>Keress premier filmeket!</strong><br>
      <br>
      Például: "Bosszúállók", "Star Wars", "Hang&nbsp;nélkül", stb.`,
    minlength: "A kereséshez legalább két betűt meg kell adnod!",
    matched: " film felelt meg a keresésnek",
    first_few: " itt az első ",
    no_match: "Sajnos nincs a keresésnek megfelelő találat!",
    searching: "<i class='fas fa-circle-notch fa-spin'></i> Keresés...",
  };

  const DEBOUNCE_DELAY = 300;

  const el = element;
  const opts = {
    minimumQueryLength: 2,
    maxMatchesToShow: null,
    ...options,
  };

  /** @type {LunrSearch|null} */
  let lunrSearch = null;
  let isLoadingLunrSearch = false;
  let settingsForm;
  let searchResultMovieList;
  let searchSubmitButton;
  let keyupWait = null;
  let previousQ = null;

  const handleSearchFieldValueChange = (event) => {
    const currentValue = event.target.value;

    // Immediate visual feedback - update UI before debounce
    window.clearTimeout(keyupWait);

    // Show immediate feedback based on current input length
    if (currentValue.length === 0) {
      searchResultMovieList.setMessage(MESSAGES.default);
      if (searchSubmitButton) searchSubmitButton.style.display = "none";
    } else if (currentValue.length < opts.minimumQueryLength) {
      // Show loading initially, will switch to minlength message if typing stops
      searchResultMovieList.setMessage(MESSAGES.searching);
      if (searchSubmitButton) searchSubmitButton.style.display = "none";
    } else {
      // Valid length - show loading indicator immediately
      searchResultMovieList.setMessage(MESSAGES.searching);
      if (searchSubmitButton) searchSubmitButton.style.display = "none";
    }

    // Debounced search execution
    keyupWait = window.setTimeout(async () => {
      // Load lunr search library if not already loaded or loading
      if (!lunrSearch && !isLoadingLunrSearch) {
        searchResultMovieList.setMessage(MESSAGES.searching);
        isLoadingLunrSearch = true;
        try {
          lunrSearch = await loadLunrSearch();
        } catch (error) {
          if (process.env.NODE_ENV === "development") console.error("Failed to load Lunr search:", error);
          searchResultMovieList.setMessage("Hiba történt a keresés betöltésekor.");
          return;
        } finally {
          isLoadingLunrSearch = false;
        }
      }

      // Wait for lunr to finish loading
      if (isLoadingLunrSearch) {
        return;
      }

      const q = lunrSearch.sanitizeTerms(currentValue);
      if (previousQ === q) {
        return;
      }
      previousQ = q;

      searchResultMovieList.reset();
      if (searchSubmitButton) {
        searchSubmitButton.style.display = "none";
        searchSubmitButton.disabled = false;
        searchSubmitButton.innerHTML = MESSAGES.button;
      }

      if (q.length === 0) {
        searchResultMovieList.setMessage(MESSAGES.default);
      } else if (q.length < opts.minimumQueryLength) {
        // User stopped typing before reaching minimum length
        searchResultMovieList.setMessage(MESSAGES.minlength);
      } else {
        const matches = lunrSearch.search(q);
        if (matches.length > 0) {
          if (opts.maxMatchesToShow && matches.length > opts.maxMatchesToShow) {
            searchResultMovieList.setMessage(
              `${matches.length}${MESSAGES.matched}, ${MESSAGES.first_few}${opts.maxMatchesToShow}`
            );
            if (searchSubmitButton) searchSubmitButton.style.display = "block";
          } else {
            searchResultMovieList.setMessage(`${matches.length}${MESSAGES.matched}:`);
          }

          matches.slice(0, opts.maxMatchesToShow || matches.length).forEach((match) => {
            const doc = lunrSearch.getDocumentByRef(match.ref);
            searchResultMovieList.addMovie({
              id: doc.id,
              slug: doc.slug,
              delayed: doc.delayed,
              soon: doc.soon,
              poster: doc.generated_poster_thumb,
              distributor: doc.distributor,
              release_date: doc.hu_release_nice,
              title: doc.hu_title,
              subtitle: doc.subtitle,
              age_limit: doc.age_limit,
              original_title: doc.original_title,
              year: doc.year,
              top_country: doc.top_country,
              genres: doc.genres,
              runtime: doc.runtime,
            });
          });
        } else {
          searchResultMovieList.setMessage(MESSAGES.no_match);
        }
      }
    }, DEBOUNCE_DELAY);
  };

  const handleKeyboardNavigation = (event) => {
    if (event.key === "Escape") {
      return component.close();
    }
    if (event.key === "ArrowUp") {
      return searchResultMovieList.selectPrevious();
    }
    if (event.key === "ArrowDown") {
      return searchResultMovieList.selectNext();
    }
    if (event.key === "Enter") {
      const selectedItem = searchResultMovieList.getSelectedItem();
      if (selectedItem) {
        window.location = selectedItem.href;
      }
    }
  };

  /** @type {HeaderSearchComponent} */
  const component = BottomSheet(el, {
    componentName: "HeaderSearch",
    scrollBody: true,
    onOpen: async () => {
      keyupWait = null;
      previousQ = null;

      settingsForm = SettingsForm(el.querySelector(".settings-form"), {
        onSubmit: (event) => {
          if (searchResultMovieList.getSelectedItem()) event.preventDefault();
        },
      }).mount();
      settingsForm.on("q", "keyup", handleKeyboardNavigation);
      settingsForm.on("q", "input search", handleSearchFieldValueChange);
      settingsForm.setValue("q", "");

      searchResultMovieList = MovieList(
        el.querySelector(".search-results"),
        el.querySelector(".search-result-message"),
        {
          selectable: true,
          inverse: true,
        }
      ).mount();
      searchResultMovieList.reset();
      searchResultMovieList.setMessage(MESSAGES.default);

      searchSubmitButton = el.querySelector(".submit-settings-form");
      if (searchSubmitButton) searchSubmitButton.style.display = "none";
    },
    onClose: async () => {
      if (searchResultMovieList) searchResultMovieList.unmount();
      if (settingsForm) settingsForm.unmount();
    },
  });

  return component;
};

export default HeaderSearch;
